// Library Methods
import { useTranslation } from "react-i18next";
import { useState, useMemo, useCallback } from "react";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useProgressionExams } from "src/services/exams/hooks/useProgressionExams";
import { useProgressionAnalyses } from "src/services/exams/hooks/progression-analyses/useProgressionAnalyses";
import { useDeleteProgressionAnalyses } from "src/services/exams/hooks/progression-analyses/useDeleteProgressionAnalyses";
import useDebounce from "src/hooks/input/useDebounce";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import ErrorText from "src/components/shared/text/ErrorText/ErrorText";
import ExpandableSearchbar from "src/components/shared/inputs/ExpandableSearchbar/ExpandableSearchbar";
import CustomIconButton from "src/components/shared/buttons/CustomIconButton.tsx/CustomIconButton";
import CustomPagination from "src/components/shared/pagination/CustomPagination/CustomPagination";
import TableHeader from "src/components/shared/tables/TableHeader/TableHeader";
import TableSkeleton from "src/components/containers/shared/TableSkeleton/TableSkeleton";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import ProgressionAnalysisCard from "./ProgressionAnalysisCard/ProgressionAnalysisCard";
import ProgressionAnalysisSlider from "./ProgressionAnalysisSlider/ProgressionAnalysisSlider";
import { PlusIcon, TrashIcon } from "src/assets/icons";

// Utils
import { countExamsForEye } from "src/utils/exams/progressionAnalysisHelper";
import {
  getItemsToRender,
  getPageCount,
} from "src/utils/pagination/paginationHelper";
import { multiKeyCompare } from "src/utils/tables/tableHelper";
import { getLocalDate, sortByOldestDate } from "src/utils/date/dateHelper";

// Constants
import { eyeTypeEnums } from "src/constants/enums";

// Types
import { ProgressionAnalysisType } from "src/services/exams/types";
import { OrderType } from "src/types/sharedTypes";
import { PatientType } from "src/services/patients/types";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";
import { CustomIconButtonDark } from "src/styles/mui/muiStylesHelper";

const MAX_NUM_OF_ROWS = 4;
const MIN_NUM_OF_VALID_EXAMS = 3;

type ExtendedAnalysisType = ProgressionAnalysisType & {
  earliestCompletionDate: string;
  latestCompletionDate: string;
};

type ProgressionAnalysesType = {
  selectedPatient: PatientType;
};

const ProgressionAnalyses = ({ selectedPatient }: ProgressionAnalysesType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { clinicInfo } = useClinicContext();

  const { LEFT, RIGHT } = eyeTypeEnums;

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Array<string>>([]);

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedTerm = useDebounce(searchTerm);

  const { data: progressionExamsData } = useProgressionExams(
    clinicInfo?.id,
    selectedPatient.id
  );

  const {
    data: analysisData,
    isLoading: isLoadingAnalyses,
    isFetching: isFetchingAnalyses,
    isRefetching: isRefetchingAnalyses,
    isError: isErrorAnalyses,
  } = useProgressionAnalyses(
    clinicInfo?.id,
    clinicInfo?.timezone,
    selectedPatient.id
  );

  const { mutate: deleteMutate, isPending: isPendingDelete } =
    useDeleteProgressionAnalyses(clinicInfo?.id, selectedPatient.id);

  const isLoading = useMemo(
    () =>
      isLoadingAnalyses ||
      isFetchingAnalyses ||
      isRefetchingAnalyses ||
      isPendingDelete,
    [
      isLoadingAnalyses,
      isFetchingAnalyses,
      isRefetchingAnalyses,
      isPendingDelete,
    ]
  );

  const hasProgressionAnalyses = useMemo(
    () =>
      !isLoading &&
      !isErrorAnalyses &&
      analysisData &&
      analysisData?.length > 0,
    [isLoading, isErrorAnalyses, analysisData]
  );

  const hasNoProgressionAnalyses = useMemo(
    () => !isLoading && !isErrorAnalyses && !analysisData?.length,
    [isLoading, isErrorAnalyses, analysisData]
  );

  const [order, setOrder] = useState<OrderType>("desc");
  const [orderByOptions, setOrderByOptions] = useState<
    Array<keyof ExtendedAnalysisType>
  >(["creationDate"]);

  const orderByEye: Array<keyof ExtendedAnalysisType> = ["eye"];
  const orderByEarliestDate: Array<keyof ExtendedAnalysisType> = [
    "earliestCompletionDate",
  ];
  const orderByCreationDate: Array<keyof ExtendedAnalysisType> = [
    "creationDate",
  ];

  const headCells = [
    {
      id: "checkbox",
      gridSize: 1,
      orderUsing: [],
      isCheckbox: true,
    },
    {
      id: "eye",
      label: t("Eye"),
      gridSize: 2,
      orderUsing: [...orderByEye],
    },
    {
      id: "earliest-completion-date",
      label: t("Completion Date Range"),
      gridSize: 5,
      orderUsing: [...orderByEarliestDate],
    },
    {
      id: "creation-date",
      label: t("Creation Date"),
      gridSize: 3,
      orderUsing: [...orderByCreationDate],
    },
    {
      id: "extra-cell",
      gridSize: 1,
      orderUsing: [],
    },
  ];

  const handleRequestSort = useCallback(
    (
      _: React.MouseEvent<HTMLButtonElement>,
      property: Array<keyof ExtendedAnalysisType>
    ) => {
      const isAsc =
        orderByOptions.length &&
        orderByOptions[0] === property[0] &&
        order === "asc";

      setOrder(isAsc ? "desc" : "asc");
      setOrderByOptions(property);
    },
    [order, orderByOptions]
  );

  const formattedAnalyses: Array<ExtendedAnalysisType> = useMemo(
    () =>
      (analysisData ?? [])?.map((analysis) => {
        // get all completion dates from both exam types
        const allExamDates = [
          ...analysis?.exams.map((e) => ({ completionDate: e.completionDate })),
          ...analysis?.externalExams.map((e) => ({
            completionDate:
              e?.completionDate ?? e?.data?.completionDate ?? undefined,
          })),
        ];

        // get exams with a valid completion date
        const filteredExams = allExamDates.filter(
          (exam) => exam.completionDate
        );

        // sort by completion date
        const sortedArray = sortByOldestDate(filteredExams, "completionDate");

        // get earliest and latest completion dates
        const earliest =
          sortedArray.length > 0 ? sortedArray[0]?.completionDate : null;
        const latest =
          sortedArray.length > 0
            ? sortedArray[sortedArray.length - 1]?.completionDate
            : null;

        return {
          ...analysis,
          earliestCompletionDate: earliest
            ? getLocalDate(earliest, clinicInfo?.timezone)
            : "N/A",
          latestCompletionDate: latest
            ? getLocalDate(latest, clinicInfo?.timezone)
            : "N/A",
        };
      }),
    [analysisData, clinicInfo?.timezone]
  );

  const filteredRows = useMemo(() => {
    let filterArray = formattedAnalyses?.filter((progressionAnalysis) => {
      const debouncedLowerCase = debouncedTerm?.toLowerCase();

      const searchTermHasEye = progressionAnalysis?.eye
        ?.toString()
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      const searchTermHasCreation = progressionAnalysis?.creationDate
        ?.toString()
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      const searchTermHasDateRange =
        `${progressionAnalysis?.earliestCompletionDate} - ${progressionAnalysis?.latestCompletionDate}`
          ?.toLowerCase()
          ?.includes(debouncedLowerCase);

      return (
        searchTermHasEye || searchTermHasCreation || searchTermHasDateRange
      );
    });

    return multiKeyCompare<ExtendedAnalysisType>(
      filterArray,
      order === "asc",
      orderByOptions
    );
  }, [formattedAnalyses, debouncedTerm, order, orderByOptions]);

  const rowsToRender = useMemo(
    () => getItemsToRender(filteredRows ?? [], page, MAX_NUM_OF_ROWS),
    [filteredRows, page]
  );

  const hasNoSearchResults = useMemo(
    () => debouncedTerm?.length > 0 && !filteredRows?.length,
    [debouncedTerm, filteredRows]
  );

  // to create a progression analysis,
  // patient must have enough valid exams for at least one eye
  const hasValidExamsForProgressionAnalysis = useMemo(
    () =>
      countExamsForEye(progressionExamsData, LEFT) >= MIN_NUM_OF_VALID_EXAMS ||
      countExamsForEye(progressionExamsData, RIGHT) >= MIN_NUM_OF_VALID_EXAMS,
    [progressionExamsData, LEFT, RIGHT]
  );

  // Clear searchbar
  const clearSearchFilter = () => {
    setSearchTerm("");
  };

  // Selects all patient card checkboxes
  const handleSelectAllClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const isChecked = (event.target as HTMLInputElement).checked;

      // select all
      if (isChecked) {
        const selectedIds = rowsToRender.map((n) => n.id);
        setSelectedForDelete([...selectedIds]);
      }
      // deselect all
      else {
        setSelectedForDelete([]);
      }
    },
    [rowsToRender, setSelectedForDelete]
  );

  // Handle checkbox clicks
  const handleCheckboxClick = useCallback(
    (_: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
      const selectedIndex = selectedForDelete.indexOf(id);
      let newSelected: Array<string> = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedForDelete, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedForDelete.slice(1));
      } else if (selectedIndex === selectedForDelete.length - 1) {
        newSelected = newSelected.concat(selectedForDelete.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedForDelete.slice(0, selectedIndex),
          selectedForDelete.slice(selectedIndex + 1)
        );
      }

      setSelectedForDelete(newSelected);
    },
    [selectedForDelete, setSelectedForDelete]
  );

  // Searchbar change
  const onSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (page > 1) setPage(1);
    setSearchTerm(event?.target?.value);
  };

  // Pagination change
  const onPaginationChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    },
    [setPage]
  );

  const deleteProgressionAnalysis = useCallback(() => {
    deleteMutate(selectedForDelete, {
      onSuccess: () => {
        setSelectedForDelete([]);
        setOpenDeleteModal(false);
      },
    });
  }, [deleteMutate, selectedForDelete]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-end",
          marginBottom: "20px",
        }}
      >
        <Box sx={{ display: "flex", gap: "8px" }}>
          {(hasProgressionAnalyses || isLoading) && (
            <ExpandableSearchbar
              id="progression-analyses-searchbar"
              isLoading={isLoading}
              searchTerm={searchTerm}
              onChange={onSearchChange}
              onClear={clearSearchFilter}
              searchTooltip={t("Search Progression Analyses")}
            />
          )}
          {Boolean(
            clinicInfo?.licensing?.canUseProgressionReports &&
              hasValidExamsForProgressionAnalysis
          ) && (
            <CustomIconButton
              isLoading={isLoading}
              tooltipTitle={t("Add Progression Analysis")}
              onClick={() => setOpenAddModal(true)}
              icon={<PlusIcon />}
            />
          )}
          {selectedForDelete?.length > 0 && (
            <CustomIconButton
              isLoading={isLoading}
              variant="error"
              tooltipTitle={t("Delete Progression Analysis", {
                count: selectedForDelete?.length,
              })}
              onClick={() => setOpenDeleteModal(true)}
              icon={<TrashIcon />}
            />
          )}
        </Box>
      </Box>

      {isLoading && <TableSkeleton numOfRows={MAX_NUM_OF_ROWS} />}

      {hasProgressionAnalyses && !hasNoSearchResults && (
        <Box display="flex" flexDirection="column" gap="12px">
          <TableHeader<ExtendedAnalysisType>
            headCells={headCells}
            order={order}
            orderBy={orderByOptions?.[0] ?? "creationDate"}
            onRequestSort={handleRequestSort}
            onSelectAllClick={handleSelectAllClick}
            rowCount={rowsToRender?.length}
            numSelected={selectedForDelete?.length}
          />
          {rowsToRender?.map((progressionAnalysis) => (
            <ProgressionAnalysisCard
              key={progressionAnalysis.id}
              progressionAnalysis={progressionAnalysis}
              patientId={selectedPatient.id}
              isSelectedForDeletion={selectedForDelete.includes(
                progressionAnalysis.id
              )}
              handleCheckboxClick={handleCheckboxClick}
            />
          ))}
          <Box mt="12px">
            <CustomPagination
              page={page}
              count={getPageCount(filteredRows?.length, MAX_NUM_OF_ROWS)}
              onChange={onPaginationChange}
            />
          </Box>
        </Box>
      )}

      {/* Add analysis modal */}
      <ProgressionAnalysisSlider
        open={openAddModal}
        patient={selectedPatient}
        onClose={() => setOpenAddModal(false)}
      />

      {/* Deletion modal */}
      <SharedModal
        title={t("Delete Progression Analysis", {
          count: selectedForDelete?.length,
        })}
        open={openDeleteModal}
        modalDescription={t(
          "Are you sure you want to delete the selected progression analysis?",
          { count: selectedForDelete?.length }
        )}
        confirmButtonText={t("Delete")}
        confirmButtonVariant="error"
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={deleteProgressionAnalysis}
      />

      {/* Error messages */}
      {!hasNoProgressionAnalyses && hasNoSearchResults && (
        <ErrorText
          header={t("No Progression Analyses Found.")}
          message={t("Change your search term to see more results.")}
        />
      )}
      {hasNoProgressionAnalyses && (
        <ErrorText
          header={t("Patient Has No Progression Analyses.")}
          message={
            hasValidExamsForProgressionAnalysis ? (
              <Typography maxWidth="400px">
                {t("Click the")}{" "}
                <IconButton
                  sx={{
                    ...CustomIconButtonDark(theme),
                    width: `${UI_SIZES.iconButtonSize}px`,
                    height: `${UI_SIZES.iconButtonSize}px`,
                    pointerEvents: "none",
                  }}
                >
                  <PlusIcon />
                </IconButton>{" "}
                {t("button above to create a progression analysis.")}
              </Typography>
            ) : (
              t(
                "At least 3 thresholding exams must have been completed to create a progression analysis."
              )
            )
          }
        />
      )}
    </Box>
  );
};

export default ProgressionAnalyses;
